export interface SupportDecisionAction {
  type: 'reply' | 'close' | 'escalate' | 'no_action' | 'tool_request';
  summary?: string;
  message?: string;
  closeTicket?: boolean;
  reason?: string;
  target?: string;
  messageLanguage?: string;
  messageTranslation?: string | null;
}

export interface SupportDecision {
  ticketId: number;
  subject: string;
  body: string | null;
  ticketTranslation: string | null;
  createdAt: string | null;
  phoneNumber: string | null;
  emailLinkedPhoneNumber: string | null;
  action: SupportDecisionAction;
  confidence: number;
  autoclose: boolean;
  attachmentDescription: string | null;
  hasVideoAttachment: boolean;
  supportCaseNames?: string[] | null;
  supportCaseName?: string | null;
  promptData?: any;
}

export const getSupportCaseNames = (decision: Pick<SupportDecision, 'supportCaseNames' | 'supportCaseName'> | null | undefined): string[] => {
  const rawNames = decision?.supportCaseNames;
  if (Array.isArray(rawNames)) {
    const seen = new Set<string>();
    const names: string[] = [];

    for (const entry of rawNames) {
      if (typeof entry !== 'string') continue;
      const trimmed = entry.trim();
      if (!trimmed) continue;

      const key = trimmed.toLowerCase();
      if (seen.has(key)) continue;

      seen.add(key);
      names.push(trimmed);
    }

    if (names.length > 0) {
      return names;
    }
  }

  const legacyName = decision?.supportCaseName?.trim();
  return legacyName ? [legacyName] : [];
};

export const formatSupportCaseNames = (decision: Pick<SupportDecision, 'supportCaseNames' | 'supportCaseName'> | null | undefined): string => {
  const names = getSupportCaseNames(decision);
  return names.length > 0 ? names.join(', ') : '—';
};

export interface ManualTicket {
  ticketId: number;
  trackedAt: number;
  decision: SupportDecision | null;
  reviewCachedAt: number | null;
}

export interface ManualTicketCollection {
  items: ManualTicket[];
}

export interface AutocloseTicket {
  ticketId: number;
  decision: SupportDecision;
  cachedAt: number;
}

export interface AutocloseTicketCollection {
  items: AutocloseTicket[];
  total: number;
  days: number;
  limit: number;
  offset: number;
}

export interface ClosedManualTicket {
  id: number;
  ticketId: number;
  action: string;
  decision: SupportDecision | null;
  actedAt: number;
}

export interface ClosedManualTicketCollection {
  items: ClosedManualTicket[];
  total: number;
  days: number;
  limit: number;
  offset: number;
}

export interface AutoModeState {
  enabled: boolean;
  manualCount: number;
}

export interface TicketActionResult {
  ok: boolean;
  ticketId: number;
  action: string;
  message: string;
  decision?: SupportDecision | null;
}

export interface FreshdeskAttachment {
  id: number;
  name?: string | null;
  content_type?: string | null;
  attachment_url?: string | null;
  size?: number | null;
}

export interface FreshdeskConversation {
  id: number;
  body: string;
  body_text?: string;
  incoming: boolean;
  private?: boolean;
  from_email?: string;
  to_emails?: string[];
  created_at: string;
  updated_at: string;
  user_id?: number;
  support_email?: string;
  source?: number;
  attachments?: FreshdeskAttachment[];
}

export interface FreshdeskTicket {
  id: number;
  subject: string;
  description?: string;
  description_text?: string;
  status: number;
  priority: number;
  type?: string;
  created_at: string;
  updated_at: string;
  requester_id?: number;
  email?: string;
  conversations?: FreshdeskConversation[];
  requester?: {
    id: number;
    name: string;
    email: string;
  };
  attachments?: FreshdeskAttachment[];
  hasVideoAttachment?: boolean;
}

export interface TicketReview {
  decision: SupportDecision;
  cachedAt: number;
}

export interface TicketDetail {
  ticketId: number;
  review: TicketReview | null;
  ticket: FreshdeskTicket | null;
}

export interface TicketReplyCount {
  ticketId: number;
  replyCount: number;
}

export interface TicketReviewItem {
  ticketId: number;
  decision: SupportDecision;
  cachedAt: number;
}

export interface TicketReviewCollection {
  items: TicketReviewItem[];
  limit: number;
  offset: number;
}

export interface SupportCase {
  id: number;
  name: string;
  instructions: string;
  autoclose: boolean;
  sortOrder: number;
  createdAt: number;
  updatedAt: number;
}

export interface CaseWithHistory {
  case: SupportCase;
  history: CaseChangeHistoryEntry[];
}

export interface CreateSupportCaseInput {
  name: string;
  instructions: string;
  autoclose?: boolean;
  sortOrder?: number;
}

export interface UpdateSupportCaseInput {
  name?: string;
  instructions?: string;
  autoclose?: boolean;
  sortOrder?: number;
}

export interface CaseChangeHistoryEntry {
  id: number;
  entityType: string;
  entityId: number;
  fieldName: string;
  oldValue: string | null;
  newValue: string | null;
  changeSummary: string | null;
  changedBy: string;
  status: string;
  reviewedBy: string | null;
  createdAt: number;
  reviewedAt: number | null;
}

export interface PromptConfig {
  key: string;
  content: string;
  createdAt: number;
  updatedAt: number;
}
